import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Camera,
  ClipboardCheck,
  FileText,
  Loader2,
  RefreshCw,
  Truck,
  UserMinus,
  UserPlus,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusBadge, type BadgeTone } from '@/components/ui/StatusBadge';
import { cn } from '@/lib/utils';
import {
  useAdminCylinderAudits,
  useDrivers,
  useRetryCylinderAudit,
  useSetDriver,
  type AdminAuditRow,
  type AuditStatus,
} from '@/hooks/admin/useCylinderAudits';

/**
 * AdminAudits — /admin/audits: oversight of the driver Cylinder Audit PWA.
 *
 *   - Every Tank Rental Audit filed (customer, driver, deltas, status),
 *     linking to the CR-ADJ invoice where the merged audit document renders.
 *   - Retry button for audits whose QBO write failed (duplicate-safe: the
 *     backend re-links if the invoice actually landed).
 *   - Driver access management — grant/revoke the is_driver flag by email,
 *     replacing the SQL-only provisioning.
 */

const FILTERS: { key: AuditStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending_approval', label: 'Pending approval' },
  { key: 'adjusted', label: 'Adjusted' },
  { key: 'balanced', label: 'Balanced' },
  { key: 'invoice_failed', label: 'Failed' },
  { key: 'declined', label: 'Declined' },
];

function AuditStatusBadge({ status }: { status: AuditStatus }) {
  const map: Record<AuditStatus, { tone: BadgeTone; label: string }> = {
    balanced: { tone: 'neutral', label: 'Balanced' },
    adjusted: { tone: 'success', label: 'Adjusted' },
    invoice_failed: { tone: 'warning', label: 'Invoice failed' },
    pending_approval: { tone: 'warning', label: 'Pending approval' },
    declined: { tone: 'neutral', label: 'Declined' },
  };
  const { tone, label } = map[status];
  return <StatusBadge tone={tone} size="sm">{label}</StatusBadge>;
}

function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function deltaSummary(audit: AdminAuditRow): string {
  const moved = (audit.counts ?? []).filter((c) => c.delta !== 0);
  if (moved.length === 0) return 'no change';
  return moved
    .map((c) => `${c.label} ${c.delta > 0 ? '+' : ''}${c.delta}`)
    .join(' · ');
}

function AuditRow({ audit }: { audit: AdminAuditRow }) {
  const retry = useRetryCylinderAudit();
  return (
    <div className="flex flex-col gap-2 border-b border-brix-border px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-bold text-brix-primary">{audit.doc_number}</span>
          <AuditStatusBadge status={audit.status} />
          <span className="text-xs text-brix-muted">{fmtWhen(audit.created_at)}</span>
        </div>
        <div className="mt-0.5 truncate text-sm font-semibold text-brix-ink">
          {audit.customer_name ?? audit.customer_id}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-brix-muted">
          <span>{deltaSummary(audit)}</span>
          <span className="inline-flex items-center gap-1">
            <Camera size={12} /> {audit.photo_count}
          </span>
          <span>signed {audit.signed_name}</span>
          {audit.driver_email ? <span>({audit.driver_email})</span> : null}
        </div>
        {audit.status === 'invoice_failed' && audit.invoice_error ? (
          <div className="mt-1 text-xs text-brix-warning">
            <span className="font-semibold">QuickBooks said:</span> {audit.invoice_error}
          </div>
        ) : null}
        {retry.error ? (
          <div className="mt-1 text-xs text-brix-warning">{retry.error.message}</div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {audit.status === 'pending_approval' ? (
          <Button asChild size="sm">
            <Link to={`/admin/audits/${audit.id}`}>
              <ClipboardCheck size={13} /> Review
            </Link>
          </Button>
        ) : (
          <Button asChild size="sm" variant="ghost">
            <Link to={`/admin/audits/${audit.id}`}>Details</Link>
          </Button>
        )}
        {audit.status === 'invoice_failed' ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={retry.isPending}
            onClick={() => retry.mutate(audit.id)}
          >
            {retry.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <RefreshCw size={13} />
            )}
            Retry invoice
          </Button>
        ) : null}
        {audit.qbo_invoice_id ? (
          <Button asChild size="sm" variant="ghost">
            <Link to={`/invoices/${audit.qbo_invoice_id}`}>
              <FileText size={13} /> Invoice
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function DriversPanel() {
  const { data: drivers, isLoading } = useDrivers();
  const setDriver = useSetDriver();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  async function grant() {
    setMessage(null);
    try {
      await setDriver.mutateAsync({ email: email.trim(), enable: true, name: name.trim() || undefined });
      setMessage(`${email.trim()} can now use the audit PWA.`);
      setEmail('');
      setName('');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }

  async function revoke(driverEmail: string | null) {
    if (!driverEmail) return;
    if (!window.confirm(`Remove driver access for ${driverEmail}?`)) return;
    setMessage(null);
    try {
      await setDriver.mutateAsync({ email: driverEmail, enable: false });
      setMessage(`Driver access removed for ${driverEmail}.`);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface shadow-card">
      <div className="flex items-center gap-2 border-b border-brix-border px-4 py-3">
        <Truck size={15} className="text-brix-primary" />
        <h2 className="font-display text-sm font-bold text-brix-ink">Drivers</h2>
        <span className="text-xs text-brix-muted">
          who can use the Cylinder Audit PWA (superadmins always can)
        </span>
      </div>

      <div className="flex flex-col gap-2 border-b border-brix-border px-4 py-3 sm:flex-row">
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="driver@brixbev.com"
          type="email"
          className="sm:max-w-xs"
        />
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (optional)"
          className="sm:max-w-[180px]"
        />
        <Button
          size="md"
          disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) || setDriver.isPending}
          onClick={() => void grant()}
        >
          {setDriver.isPending ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
          Grant driver access
        </Button>
      </div>
      {message ? <div className="px-4 py-2 text-xs text-brix-muted">{message}</div> : null}

      {isLoading ? (
        <div className="flex items-center gap-2 px-4 py-4 text-sm text-brix-muted">
          <Loader2 size={14} className="animate-spin" /> Loading drivers…
        </div>
      ) : (drivers ?? []).length === 0 ? (
        <div className="px-4 py-4 text-sm text-brix-muted">
          No drivers yet. The account must already exist (invite flow) — this only grants the flag.
        </div>
      ) : (
        (drivers ?? []).map((d) => (
          <div
            key={d.id}
            className="flex items-center justify-between border-b border-brix-border px-4 py-2.5 last:border-b-0"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-brix-ink">{d.full_name}</div>
              <div className="truncate text-xs text-brix-muted">{d.email ?? d.id}</div>
            </div>
            <Button
              size="sm"
              variant="subtle"
              disabled={setDriver.isPending || !d.email}
              onClick={() => void revoke(d.email)}
            >
              <UserMinus size={13} /> Remove
            </Button>
          </div>
        ))
      )}
    </div>
  );
}

export default function AdminAudits() {
  const [filter, setFilter] = useState<AuditStatus | 'all'>('all');
  const { data: audits, isLoading, isFetching, error } = useAdminCylinderAudits(filter);

  return (
    <div className="space-y-5 px-6 py-6">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={cn(
              'px-3 py-1.5 text-sm rounded-lg cursor-pointer transition-all duration-200',
              filter === key
                ? 'bg-brix-primary-tint text-brix-primary font-semibold'
                : 'text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink',
            )}
          >
            {label}
          </button>
        ))}
        {isFetching && (
          <div className="ml-1 flex items-center gap-1.5 text-xs text-brix-muted">
            <Loader2 size={12} className="animate-spin" /> Loading
          </div>
        )}
      </div>

      <div className="rounded-xl border border-brix-border bg-brix-surface shadow-card">
        <div className="flex items-center gap-2 border-b border-brix-border px-4 py-3">
          <ClipboardCheck size={15} className="text-brix-primary" />
          <h2 className="font-display text-sm font-bold text-brix-ink">Cylinder audits</h2>
          <span className="text-xs text-brix-muted">
            filed from the driver PWA · open the invoice to see photos + signature
          </span>
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 px-4 py-6 text-sm text-brix-muted">
            <Loader2 size={14} className="animate-spin" /> Loading audits…
          </div>
        ) : error ? (
          <div className="px-4 py-6 text-sm text-brix-warning">
            {error instanceof Error ? error.message : 'Failed to load audits'}
          </div>
        ) : (audits ?? []).length === 0 ? (
          <div className="px-4 py-6 text-sm text-brix-muted">
            No audits {filter === 'all' ? 'yet' : `with status “${filter}”`}. Drivers file them
            from <span className="font-mono">alamedapointbg.com/audit</span>.
          </div>
        ) : (
          (audits ?? []).map((a) => <AuditRow key={a.id} audit={a} />)
        )}
      </div>

      <DriversPanel />
    </div>
  );
}
